import { type ReactNode, useMemo } from 'react'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { CleanThemeProvider } from 'shared/theme/cleanThemeProvider'
import { useTranslation } from 'shared/i18n'
import { getTenantNameFromAddressbar, getTenantStorageUrl } from 'shared/utils/url'
import { RouterLink } from 'shared/routes/components'

// Figma: 利用規約 / プライバシーポリシー 共通レイアウト
// - 上部にロゴだけのヘッダー / 背景 #ededed / 中央に最大1000pxのカード(タイトルバー + 本文) / 下部にフッター
interface DocumentLayoutProps {
  title: string
  children: ReactNode
}

const FONT = {
  fontFamily: '"Noto Sans JP", sans-serif',
  fontWeight: 500
} as const

const FOOTER_LINK_SX = {
  ...FONT,
  fontSize: '12px',
  lineHeight: 1,
  color: 'rgba(0, 0, 0, 0.6)',
  textDecoration: 'none',
  whiteSpace: 'nowrap'
} as const

function DocumentLayoutContent ({ title, children }: DocumentLayoutProps): JSX.Element {
  const { t } = useTranslation()
  const tenant = getTenantNameFromAddressbar(window.location.href)
  const companyLink = useMemo(
    () => (tenant === 'sooki' ? 'https://sooki.co.jp/' : 'https://jizaie.co.jp/'),
    [tenant]
  )
  const helpCenterLink = useMemo(
    () => (tenant === 'sooki' ? `${getTenantStorageUrl(tenant)}/public/manual.pdf` : 'https://help.jizaipad.com/ja'),
    [tenant]
  )

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#ededed' }}>
      {/* ヘッダー: ロゴのみ */}
      <Box
        component="header"
        sx={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          bgcolor: '#fcfcfc',
          borderBottom: '1px solid #ededed',
          pl: 2.5,
          pr: 1.5
        }}
      >
        {/* ロゴは元画像の縦横比 (224x31) を保つよう height に比例した width を明示する。
            width 未指定だと SVG の intrinsic 幅で横に間延びして表示される。 */}
        <Box alt="JIZAIPAD" component="img" src="/assets/logos/jizaipad-logo.svg" sx={{ height: 16, width: 116 }} />
      </Box>

      {/* 本体: 中央のカード */}
      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', p: 2, overflowY: 'auto' }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 1000,
            bgcolor: '#fcfcfc',
            borderRadius: '8px',
            alignSelf: 'flex-start'
          }}
        >
          {/* タイトルバー */}
          <Box
            sx={{
              height: 62,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              px: 3
            }}
          >
            <Typography sx={{ ...FONT, fontSize: 20, color: 'rgba(0,0,0,0.87)' }}>{title}</Typography>
          </Box>
          {/* 本文: Figma の文字サイズに合わせて MUI variant を上書き */}
          <Box
            sx={{
              px: 3,
              pt: 1,
              pb: 3,
              color: 'rgba(0,0,0,0.87)',
              fontFamily: '"Noto Sans JP", sans-serif',
              // 章
              '& .MuiTypography-h3': { fontSize: '16px', fontWeight: 500, lineHeight: 1.5, mt: 3, mb: 1 },
              // 条
              '& .MuiTypography-h4': { fontSize: '14px', fontWeight: 500, lineHeight: 1.5, mt: 2, mb: 0.5 },
              // 本文(章/条の h3/h4 を巻き込まないよう body1 に限定)
              '& .MuiTypography-body1, & p, & li, & span': { fontSize: '14px', fontWeight: 400, lineHeight: 1.6 },
              '& ol': { pl: 3 }
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>

      {/* フッター: AuthLayout と同じく md+ は grid 3カラム、xs は縦積みで重なりを防ぐ */}
      <Box
        component="footer"
        sx={{
          minHeight: 48,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
          alignItems: 'center',
          justifyItems: 'center',
          rowGap: 1,
          bgcolor: '#fcfcfc',
          borderTop: '1px solid #ededed',
          px: 2.5,
          py: { xs: 1.5, md: 0 }
        }}
      >
        {/* 左余白(md+のみ): 中央ナビを真ん中に保つ */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', justifyContent: 'center' }}>
          <Link component={RouterLink} href="/login" sx={FOOTER_LINK_SX}>{t('shared.documentLayout.link')}</Link>
          <Link href={helpCenterLink} rel="noopener noreferrer" sx={FOOTER_LINK_SX} target="_blank">{t('shared.documentLayout.link2')}</Link>
          <Link component={RouterLink} href="/terms-of-use" sx={FOOTER_LINK_SX}>{t('shared.documentLayout.link3')}</Link>
          <Link component={RouterLink} href="/privacy-policy" sx={FOOTER_LINK_SX}>{t('shared.documentLayout.link4')}</Link>
          <Link href={companyLink} rel="noopener noreferrer" sx={FOOTER_LINK_SX} target="_blank">{t('shared.documentLayout.link5')}</Link>
        </Box>
        <Box sx={{ justifySelf: { xs: 'center', md: 'end' }, ...FONT, fontSize: '11px', color: 'rgba(0,0,0,0.6)', whiteSpace: 'nowrap' }}>
          © 2025. JIZAIE,Inc. All rights reserved
        </Box>
      </Box>
    </Box>
  )
}

export default function DocumentLayout (props: DocumentLayoutProps): JSX.Element {
  return (
    <CleanThemeProvider>
      <DocumentLayoutContent {...props} />
    </CleanThemeProvider>
  )
}
